import React, { useState, useEffect } from 'react';
import { Form, Select, DatePicker, Input, Button, Row, Col, message, AutoComplete, Tag, Space } from 'antd';
import { Notice } from '../common/Notice';
import dayjs, { Dayjs } from 'dayjs';
import {
  Gender,
  EducationLevel,
  EconomicLevel,
  CreateProfileData,
  UpdateProfileData,
  State,
  City,
} from '../../types/profile.types';
import { AgeRange, calculateAgeRange, getAgeRangeLabel } from '../../utils/age-range';
import { profileService } from '../../services/profile.service';
import { AsyncState } from '../common/AsyncState';
import { EconomicLevelInfoIcon } from '../common/EconomicLevelInfo';
import type { ValidationLevel } from '../../types/validation.types';

// Phase 3 - US1: Profile Form Component
// T052: ProfileForm with validation

const { Option } = Select;

function parseE164(value: string | undefined): { ddd: string; number: string } {
  if (!value || !value.startsWith('+55')) return { ddd: '', number: '' };
  const digits = value.slice(3);
  return { ddd: digits.slice(0, 2), number: digits.slice(2) };
}

function buildE164(ddd: string, number: string): string {
  const raw = ddd + number;
  return raw ? '+55' + raw : '';
}

function applyNumberMask(raw: string): string {
  if (raw.length <= 4) return raw;
  if (raw.length <= 8) return raw.slice(0, 4) + '-' + raw.slice(4);
  return raw.slice(0, 5) + '-' + raw.slice(5, 9);
}

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, disabled }) => {
  const parsed = parseE164(value);
  const [ddd, setDdd] = useState(parsed.ddd);
  const [number, setNumber] = useState(parsed.number);

  useEffect(() => {
    const p = parseE164(value);
    if (p.ddd !== ddd) setDdd(p.ddd);
    if (p.number !== number) setNumber(p.number);
  }, [value]);

  const handleDddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDdd(raw);
    onChange?.(buildE164(raw, number));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 9);
    setNumber(raw);
    onChange?.(buildE164(ddd, raw));
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        value="🇧🇷 +55"
        readOnly
        disabled={disabled}
        tabIndex={-1}
        style={{ width: '90px', flexShrink: 0, cursor: 'default', color: 'rgba(0,0,0,0.88)' }}
      />
      <Input
        value={ddd}
        onChange={handleDddChange}
        placeholder="47"
        maxLength={2}
        disabled={disabled}
        inputMode="numeric"
        aria-label="DDD"
        style={{ width: '60px', flexShrink: 0 }}
      />
      <Input
        value={applyNumberMask(number)}
        onChange={handleNumberChange}
        placeholder="99999-9999"
        disabled={disabled}
        inputMode="numeric"
        aria-label="Número de telefone"
        style={{ flex: 1 }}
      />
    </Space.Compact>
  );
};

const MINIMUM_PARTICIPANT_AGE = 16;
const MAXIMUM_PARTICIPANT_AGE = 133;

interface ProfileFormProps {
  initialData?: UpdateProfileData;
  onSubmit: (data: CreateProfileData | UpdateProfileData) => Promise<void>;
  isUpdate?: boolean;
  showSubmitButton?: boolean;
  onValuesChange?: (changedValues: any, allValues: any) => void;
  formId?: string;
  validationLevel?: ValidationLevel | null;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  initialData,
  onSubmit,
  isUpdate = false,
  showSubmitButton = true,
  onValuesChange,
  formId,
  validationLevel,
}) => {
  const [form] = Form.useForm();
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<string>('');
  const [citySearchText, setCitySearchText] = useState<string>('');
  const [searchingCities, setSearchingCities] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedAgeRange, setSelectedAgeRange] = useState<AgeRange | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [cityInputValue, setCityInputValue] = useState<string>('');

  const currentPhone = Form.useWatch('phone', form) as string | undefined;
  const hasChangedPhone =
    isUpdate
    && Boolean(initialData?.phone)
    && Boolean(currentPhone)
    && currentPhone !== initialData?.phone;
  const isPhoneValidated = validationLevel ? validationLevel !== 'LEVEL_1' : false;

  useEffect(() => {
    loadStates();

    // Set initial form values if provided
    if (initialData) {
      const formValues = {
        ...initialData,
        dateOfBirth: initialData.dateOfBirth ? dayjs(initialData.dateOfBirth) : undefined,
      };
      form.setFieldsValue(formValues);

      if (initialData.dateOfBirth) {
        setSelectedAgeRange(calculateAgeRange(initialData.dateOfBirth));
      }

      // If we have city data with state information, use it directly
      if (initialData.city?.state) {
        setSelectedCity(initialData.city);
        setSelectedStateId(initialData.city.state.id);
        form.setFieldValue('stateId', initialData.city.state.id);
      }
      // Otherwise, if we have cityId, fetch the city data
      else if (initialData.cityId) {
        loadCityData(initialData.cityId);
      }
    }
  }, [initialData, form]);

  useEffect(() => {
    if (selectedCity) {
      setCityInputValue(selectedCity.name);
    }
  }, [selectedCity]);

  const loadCityData = async (cityId: string) => {
    try {
      const city = await profileService.getCityById(cityId);
      setSelectedCity(city);
      if (city.state?.id) {
        setSelectedStateId(city.state.id);
        form.setFieldValue('stateId', city.state.id);
        // Set the cities list for the state
        try {
          const citiesList = await profileService.getCitiesByState(city.state.id, '');
          setCities(citiesList);
        } catch (error) {
          console.error('Failed to load cities:', error);
        }
      }
    } catch (error) {
      message.error('Falha ao carregar dados da cidade');
      console.error('Failed to load city data:', error);
    }
  };

  // Load cities when state changes or search text changes (with debounce)
  useEffect(() => {
    if (!selectedStateId) return;

    // Only search if we have at least 2 characters or if it's empty (initial load)
    if (citySearchText.length >= 2 || citySearchText.length === 0) {
      const timer = setTimeout(() => {
        searchCities(citySearchText, selectedStateId);
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    } else {
      // Clear cities if search text is 1 character
      setCities([]);
    }
  }, [selectedStateId, citySearchText]);

  const loadStates = async () => {
    try {
      const data = await profileService.getStates();
      setStates(data);
    } catch (error) {
      message.error('Falha ao carregar estados');
      console.error('Failed to load states:', error);
    }
  };

  const searchCities = async (searchText: string, stateId?: string) => {
    const targetStateId = stateId || selectedStateId;
    if (!targetStateId) return;

    try {
      setSearchingCities(true);
      const data = await profileService.getCitiesByState(targetStateId, searchText);
      setCities(data);
    } catch (error) {
      message.error('Falha ao buscar cidades');
      console.error('Failed to search cities:', error);
    } finally {
      setSearchingCities(false);
    }
  };

  const handleStateChange = (stateId: string) => {
    setSelectedStateId(stateId);
    // Clear city field and cities list when state changes
    form.setFieldValue('cityId', undefined);
    setSelectedCity(null);
    setCities([]);
    setCitySearchText('');
    setCityInputValue('');
  };

  const handleDateOfBirthChange = (date: Dayjs | null) => {
    if (date) {
      const ageRange = calculateAgeRange(date.toDate());
      setSelectedAgeRange(ageRange);
    } else {
      setSelectedAgeRange(null);
    }
  };

  const handleCitySearch = (value: string) => {
    setCitySearchText(value);
    setCityInputValue(value);
  };

  const handleCitySelect = (_value: string, option: any) => {
    const cityId = option?.id as string | undefined;
    if (cityId) {
      // Store cityId in form while displaying city name in the input
      form.setFieldValue('cityId', cityId);
    }
    // Find the selected city object to update state
    const selected =
      cities.find((c) => c.id === cityId)
      || (selectedCity?.id === cityId ? selectedCity : undefined);
    if (selected) {
      setSelectedCity(selected);
      setCityInputValue(selected.name);
    }
  };

  const handleFinish = async (values: any) => {
    setApiError(null);
    setLoading(true);
    try {
      // Remove stateId from submission (not needed by backend, only for form navigation)
      const { stateId, ...profileData } = values;

      const dataToSubmit = {
        ...profileData,
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.format('YYYY-MM-DD') : undefined,
      };

      await onSubmit(dataToSubmit);
      message.success(isUpdate ? 'Perfil atualizado com sucesso!' : 'Perfil criado com sucesso!');
    } catch (error: any) {
      const raw: string = error?.response?.data?.message || error?.response?.data?.error || '';
      const KNOWN_ERRORS: Record<string, string> = {
        'Phone already in use': 'Este telefone já está cadastrado em outra conta.',
        'Invalid phone number format': 'Formato de telefone inválido.',
        'Invalid phone number': 'Número de telefone inválido.',
        'Invalid date of birth': 'Data de nascimento inválida.',
        'Participant must be at least 16 years old': 'Você deve ter pelo menos 16 anos.',
        'Participant age cannot exceed 133 years': 'A idade máxima permitida é de 133 anos.',
      };
      const errorMessage = KNOWN_ERRORS[raw] || raw || 'Falha ao salvar perfil';
      setApiError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateAge = (_: any, value: Dayjs) => {
    if (!value) {
      return Promise.reject(new Error('Data de nascimento é obrigatória'));
    }
    const age = dayjs().diff(value, 'year');
    if (age < MINIMUM_PARTICIPANT_AGE) {
      return Promise.reject(new Error('Você deve ter pelo menos 16 anos'));
    }
    if (age > MAXIMUM_PARTICIPANT_AGE) {
      return Promise.reject(new Error('A idade máxima permitida é de 133 anos'));
    }
    return Promise.resolve();
  };

  return (
    <AsyncState loading={loading} error={apiError} onRetry={() => window.location.reload()}>
      <Form
        id={formId}
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={onValuesChange}
        requiredMark="optional"
      >
        {isUpdate && (
          <div className="mb-3 rounded-xl border border-slate-200 p-4 md:p-5">
            <h3 className="mb-3 text-base font-semibold text-slate-800">Dados da conta</h3>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="preferredName"
                  label="Nome de exibição"
                  rules={[
                    { required: true, message: 'Por favor, informe seu nome de exibição' },
                    { min: 2, message: 'Nome de exibição deve ter pelo menos 2 caracteres' },
                    { max: 100, message: 'Nome de exibição deve ter no máximo 100 caracteres' },
                  ]}
                >
                  <Input placeholder="Digite seu nome de exibição" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="phone"
                  label={(
                    <span>
                      Telefone{' '}
                      <Tag color={isPhoneValidated ? 'green' : 'orange'} style={{ marginLeft: 6 }}>
                        {isPhoneValidated ? 'Validado' : 'Não validado'}
                      </Tag>
                    </span>
                  )}
                  rules={[
                    { required: true, message: 'Por favor, informe seu telefone' },
                    {
                      pattern: /^\+55\d{2}\d{8,9}$/,
                      message: 'Telefone inválido. Informe DDD + 8 ou 9 dígitos.',
                    },
                  ]}
                  extra="DDD + número com 8 ou 9 dígitos. Ex: 47 + 99999-9999"
                >
                  <PhoneInput disabled={loading} />
                </Form.Item>
              </Col>

              {hasChangedPhone && (
                <Col xs={24}>
                  <Notice
                    type="warning"
                    showIcon
                    message="Ao salvar a troca de telefone, será necessário validar o novo número."
                    description="Se seu perfil estava no nível 2 ou superior, ele ficará temporariamente como não validado até a nova validação por código."
                  />
                </Col>
              )}
            </Row>
          </div>
        )}

        <div className="mb-3 rounded-xl border border-slate-200 p-4 md:p-5">
          <h3 className="mb-3 text-base font-semibold text-slate-800">Dados pessoais</h3>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="gender"
                label="Gênero"
                rules={[{ required: true, message: 'Por favor, selecione seu gênero' }]}
              >
                <Select placeholder="Selecione seu gênero">
                  <Option value={Gender.MALE}>Masculino</Option>
                  <Option value={Gender.FEMALE}>Feminino</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="dateOfBirth"
                label="Data de Nascimento"
                rules={[
                  { required: true, message: 'Por favor, selecione sua data de nascimento' },
                  { validator: validateAge }
                ]}
                extra={selectedAgeRange ? `Faixa etária: ${getAgeRangeLabel(selectedAgeRange)}` : undefined}
              >
                <DatePicker
                  placeholder="DD/MM/AAAA"
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                  style={{ width: '100%' }}
                  disabledDate={(current) => !!current
                    && (
                      current > dayjs().subtract(MINIMUM_PARTICIPANT_AGE, 'year').endOf('day')
                      || current < dayjs().subtract(MAXIMUM_PARTICIPANT_AGE, 'year').startOf('day')
                    )}
                  onChange={handleDateOfBirthChange}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="mb-3 rounded-xl border border-slate-200 p-4 md:p-5">
          <h3 className="mb-3 text-base font-semibold text-slate-800">Perfil socioeconômico</h3>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="educationLevel"
                label="Nível de Escolaridade"
                rules={[{ required: true, message: 'Por favor, selecione sua escolaridade' }]}
              >
                <Select placeholder="Selecione sua escolaridade">
                  <Option value={EducationLevel.FUNDAMENTAL}>Ensino Fundamental</Option>
                  <Option value={EducationLevel.MEDIO}>Ensino Médio</Option>
                  <Option value={EducationLevel.SUPERIOR}>Ensino Superior</Option>
                  <Option value={EducationLevel.POS_GRADUACAO}>Pós-Graduação</Option>
                  <Option value={EducationLevel.MESTRADO}>Mestrado</Option>
                  <Option value={EducationLevel.DOUTORADO}>Doutorado</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="economicLevel"
                label={
                  <span>
                    Classe Econômica <EconomicLevelInfoIcon />
                  </span>
                }
                rules={[{ required: true, message: 'Por favor, selecione sua classe econômica' }]}
              >
                <Select placeholder="Selecione sua faixa de renda">
                  <Option value={EconomicLevel.NO_INCOME}>Sem rendimento</Option>
                  <Option value={EconomicLevel.UP_TO_HALF_MW}>Até 1/2 Salário Mínimo</Option>
                  <Option value={EconomicLevel.ONE_TO_TWO_MW}>1 a 2 Salários Mínimos</Option>
                  <Option value={EconomicLevel.TWO_TO_FIVE_MW}>2 a 5 Salários Mínimos</Option>
                  <Option value={EconomicLevel.FIVE_TO_TEN_MW}>5 a 10 Salários Mínimos</Option>
                  <Option value={EconomicLevel.TEN_TO_TWENTY_MW}>10 a 20 Salários Mínimos</Option>
                  <Option value={EconomicLevel.ABOVE_TWENTY_MW}>Acima de 20 Salários Mínimos</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 md:p-5">
          <h3 className="mb-3 text-base font-semibold text-slate-800">Localização</h3>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="stateId"
                label="Estado"
                rules={[{ required: true, message: 'Por favor, selecione seu estado' }]}
              >
                <Select
                  placeholder="Selecione seu estado"
                  onChange={handleStateChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {states.map((state) => (
                    <Option key={state.id} value={state.id}>
                      {state.name} ({state.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item shouldUpdate>
                {() => {
                  const cityErrors = form.getFieldError('cityId');
                  const cityHelp = cityErrors[0]
                    || (!selectedStateId
                      ? 'Selecione um estado primeiro'
                      : 'Digite para buscar ou clique na seta para ver todas');

                  return (
                    <Form.Item
                      label="Cidade"
                      required
                      validateStatus={cityErrors.length ? 'error' : undefined}
                      help={cityHelp}
                    >
                      <Form.Item
                        name="cityId"
                        rules={[{ required: true, message: 'Por favor, selecione sua cidade' }]}
                        noStyle
                      >
                        <Input type="hidden" />
                      </Form.Item>
                      <AutoComplete
                        placeholder={selectedStateId ? 'Digite para buscar cidade' : 'Selecione o estado primeiro'}
                        disabled={!selectedStateId}
                        onSearch={handleCitySearch}
                        onSelect={handleCitySelect}
                        value={cityInputValue}
                        notFoundContent={searchingCities ? 'Buscando...' : 'Nenhuma cidade encontrada'}
                        options={[
                          ...(selectedCity ? [{ value: selectedCity.name, label: selectedCity.name, id: selectedCity.id }] : []),
                          ...cities.filter(c => !selectedCity || c.id !== selectedCity.id).map(city => ({
                            value: city.name,
                            label: city.name,
                            id: city.id,
                          })),
                        ]}
                        onFocus={() => {
                          if (selectedStateId && cities.length === 0) {
                            void searchCities('', selectedStateId);
                          }
                        }}
                        filterOption={false}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="neighborhood"
                label="Bairro"
                rules={[
                  { required: true, message: 'Por favor, informe seu bairro' },
                  { min: 2, message: 'Bairro deve ter pelo menos 2 caracteres' },
                  { max: 100, message: 'Bairro deve ter no máximo 100 caracteres' },
                ]}
              >
                <Input placeholder="Digite seu bairro" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {showSubmitButton && (
          <Form.Item style={{ marginTop: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              {isUpdate ? 'Atualizar Perfil' : 'Criar Perfil'}
            </Button>
          </Form.Item>
        )}
      </Form>
    </AsyncState>
  );
};
